import { Loadable } from '@/components/loaders';
import { setSmsCampaignsPagination } from '@/features/campaigns/campaignsSlice';
import { useSmsWorkspaceTemplates } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { FrontTable, IconButton, PlusIcon, TablePager } from '@/lib';
import { RootState } from '@/reducers';
import { useGetCampaignsCountV2Query, useLazyGetCampaignsV2Query } from '@/services/endpoints/campaigns';
import { CampaignV2Response } from '@/services/endpoints/campaigns/types';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CampaignListItem, getSmsCampaignListColumns } from './getSmsCampaignListColumns';
import { mapCampaignStatus } from '../../Whatsapp/Campaigns/campaignStatus';
import { SmsCampaignPrerequisiteBanner } from './SmsCampaignPrerequisiteBanner';

const MINIMUM_LOADING_DURATION = 400;

const formatCampaignSubtitle = (campaign: CampaignV2Response) => {
  const sourceDate = campaign.scheduledStart ?? campaign.createdOn;
  if (!sourceDate) {
    return 'N/A';
  }

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(sourceDate));

  return `${dateLabel} · ${campaign.providerType}`;
};

const toCampaignListItem = (campaign: CampaignV2Response): CampaignListItem => {
  const sent = campaign.stats?.totalSent;
  const delivered = campaign.stats?.totalDelivered;
  const read = campaign.stats?.totalRead;
  const deliveredPercent = sent && delivered !== undefined ? Math.round((delivered / sent) * 100) : undefined;
  const readPercent = delivered && read !== undefined ? Math.round((read / delivered) * 100) : undefined;

  return {
    id: campaign.id,
    name: campaign.name,
    subtitle: formatCampaignSubtitle(campaign),
    status: mapCampaignStatus(campaign.status),
    sent,
    delivered,
    deliveredPercent,
    read,
    readPercent,
  };
};

export const SmsCampaignList: FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignV2Response[]>([]);

  const { t } = useTranslation();
  const history = useHistory();
  const workspace = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin();
  const { templatesCount, hasFetched, isLoading: isTemplatesLoading } = useSmsWorkspaceTemplates();
  const canCreateCampaign = hasFetched && !isTemplatesLoading && templatesCount > 0;
  const showNoTemplatesBanner = hasFetched && !isTemplatesLoading && templatesCount === 0;

  const [getCampaignsV2, { data, isFetching: isCampaignsFetching, isLoading: isCampaignsLoading, isUninitialized: isCampaignsUninitialized }] =
    useLazyGetCampaignsV2Query();

  const trimmedSearch = searchValue.trim();
  const validSearchText = trimmedSearch.length > 2 ? trimmedSearch : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetCampaignsCountV2Query({
    workspaceId: workspace.id,
    searchText: validSearchText,
    providerType: 'SMS',
  });

  const isLoadingDelayed = useDelayedBoolean(isCampaignsLoading, MINIMUM_LOADING_DURATION);
  const shouldShowLoading = isCampaignsLoading || isLoadingDelayed || isCampaignsUninitialized;
  const isFetching = isCampaignsFetching;

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getCampaignsV2({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
        providerType: 'SMS',
      }),
    [getCampaignsV2, validSearchText, workspace.id]
  );

  const campaignItems = useMemo(() => campaigns.map(toCampaignListItem), [campaigns]);

  const columns = useMemo(() => getSmsCampaignListColumns(t, { campaigns, isWorkspaceAdmin }), [t, campaigns, isWorkspaceAdmin]);

  return (
    <section className='bg-surface space-y-5 rounded-3xl border border-secondary-border p-5 shadow-sm md:p-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='space-y-1'>
          <h2 className='text-4xl font-semibold tracking-tight text-slate-900'>
            <Trans
              i18nKey='campaign:smsWorkspace.campaigns.heroTitle'
              components={{
                accent: <span className='text-blue-800' />,
              }}
            />
          </h2>
          <p className='text-secondary-600 max-w-2xl text-sm'>{t('campaign:smsWorkspace.campaigns.heroSubtitle')}</p>
        </div>
        <IconButton
          disabled={!canCreateCampaign}
          leadingIcon={<PlusIcon className='size-5 text-white-100' />}
          className='inline-flex w-full max-w-[211px] justify-center capitalize'
          onClick={() => history.push(RouteConstants.createCampaignSms)}
          text={t('campaign:smsWorkspace.campaigns.createCampaign')}
        />
      </div>

      {showNoTemplatesBanner ? <SmsCampaignPrerequisiteBanner /> : null}

      <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex w-full flex-col gap-2 sm:flex-row lg:w-auto'>
          <input
            type='text'
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={t('campaign:searchCampaign')}
            className='bg-white text-secondary-500 h-10 w-full rounded-lg border border-primary-border px-3 text-sm focus:outline-none sm:w-[300px]'
          />
        </div>
      </div>

      <TablePager
        stateSelector={(state: RootState) => state.campaigns.ui.smsCampaigns?.pagination ?? { pageNo: 0, pageSize: 0 }}
        stateUpdater={setSmsCampaignsPagination}
        query={queryCallback}
        queryResponse={data ? data.items : undefined}
        setItems={setCampaigns}
        totalCount={totalCount ?? 0}
        querying={isTotalCountFetching || isFetching}
        wrapperClassNames='mb-0'
      >
        {(pager) => (
          <Loadable isLoading={shouldShowLoading}>
            <FrontTable
              className='overflow-hidden'
              tableData={campaignItems}
              columns={columns}
              footer={pager}
              notFound={t('campaign:tableHeaders.noCampaignsFound')}
              onRowClick={(row) => history.push(RouteConstants.smsCampaignDetails.replace(':campaignId', row.original.id))}
            />
          </Loadable>
        )}
      </TablePager>
    </section>
  );
};
